import { useMemo } from 'react';
import type { RefObject } from 'react';

export type StickyElementRefs = {
  top?: RefObject<HTMLElement> | null;
  right?: RefObject<HTMLElement> | null;
  bottom?: RefObject<HTMLElement> | null;
  left?: RefObject<HTMLElement> | null;
};

export type UseStickyContentBoxParam<
  OverflowBoxElementType extends HTMLElement,
> = {
  overflowBoxRef: RefObject<OverflowBoxElementType>;
  stickyElementRefs: StickyElementRefs;
};

export const useStickyContentBox = <
  OverflowBoxElementType extends HTMLElement,
>({
  overflowBoxRef,
  stickyElementRefs,
}: UseStickyContentBoxParam<OverflowBoxElementType>) => {
  const overflowBoxElement = overflowBoxRef.current;
  let top = 0,
    right = 0,
    bottom = 0,
    left = 0;

  if (overflowBoxElement) {
    const overflowBoxBounds = overflowBoxElement.getBoundingClientRect();

    const stickyTopElement = stickyElementRefs.top?.current ?? null;
    const stickyRightElement = stickyElementRefs.right?.current ?? null;
    const stickyBottomElement = stickyElementRefs.bottom?.current ?? null;
    const stickyLeftElement = stickyElementRefs.left?.current ?? null;

    if (stickyTopElement) {
      const overflowBoxTopEdge = overflowBoxBounds.top;
      const stickyTopElementInnerEdge =
        stickyTopElement.getBoundingClientRect().bottom;
      top = stickyTopElementInnerEdge - overflowBoxTopEdge;
    }

    if (stickyRightElement) {
      const overflowBoxRightEdge = overflowBoxBounds.right;
      const stickyRightElementInnerEdge =
        stickyRightElement.getBoundingClientRect().left;
      right = overflowBoxRightEdge - stickyRightElementInnerEdge;
    }

    if (stickyBottomElement) {
      const overflowBoxBottomEdge = overflowBoxBounds.bottom;
      const stickyBottomElementInnerEdge =
        stickyBottomElement.getBoundingClientRect().top;
      bottom = overflowBoxBottomEdge - stickyBottomElementInnerEdge;
    }

    if (stickyLeftElement) {
      const overflowBoxLeftEdge = overflowBoxBounds.left;
      const stickyLeftElementInnerEdge =
        stickyLeftElement.getBoundingClientRect().right;
      left = stickyLeftElementInnerEdge - overflowBoxLeftEdge;
    }
  }

  const stickyContentBox = useMemo(
    () => ({ top, right, bottom, left }),
    [top, right, bottom, left],
  );

  return { stickyContentBox };
};
